import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Watch, 
  Heart, 
  Zap, 
  Wifi, 
  Bluetooth, 
  Battery, 
  Settings, 
  Plus, 
  Trash2, 
  RefreshCw, 
  TrendingUp, 
  Activity, 
  Moon, 
  Sun, 
  Droplets, 
  Thermometer, 
  Gauge, 
  Target, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  BarChart3, 
  Play, 
  Pause, 
  Square as Stop,
  Download,
  Upload,
  Eye,
  EyeOff
} from 'lucide-react';

interface WearableDevice {
  id: string;
  name: string;
  type: 'fitness_tracker' | 'smartwatch' | 'heart_rate_monitor' | 'power_meter' | 'gps_device';
  manufacturer: string;
  model: string;
  connectionStatus: 'connected' | 'disconnected' | 'error';
  batteryLevel: number;
  lastSync: Date;
  capabilities: string[];
  data: {
    heartRate: number;
    steps: number;
    calories: number;
    distance: number;
    activeMinutes: number;
  };
}

interface RecoveryDevice {
  id: string;
  name: string;
  type: 'massage_chair' | 'normatec' | 'compression_boots' | 'sauna' | 'ice_bath' | 'foam_roller' | 'theragun' | 'hyperice';
  manufacturer: string;
  model: string;
  connectionStatus: 'connected' | 'disconnected' | 'error';
  lastSession: Date;
  capabilities: string[];
  currentSession?: {
    duration: number;
    intensity: number;
    mode: string;
    targetArea: string[];
  };
}

interface DeviceIntegrationPanelProps {
  userId: string;
  userProfile: any;
  onDeviceConnected?: (device: any) => void;
  onDataSynced?: (data: any) => void;
  onRecoverySessionStarted?: (session: any) => void;
}

export const DeviceIntegrationPanel: React.FC<DeviceIntegrationPanelProps> = ({
  userId,
  userProfile,
  onDeviceConnected,
  onDataSynced,
  onRecoverySessionStarted
}) => {
  const [activeTab, setActiveTab] = useState<'wearables' | 'recovery' | 'analytics' | 'settings'>('wearables');
  const [wearableDevices, setWearableDevices] = useState<WearableDevice[]>([]);
  const [recoveryDevices, setRecoveryDevices] = useState<RecoveryDevice[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [showAddDevice, setShowAddDevice] = useState(false);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    // Mock data - in production, this would come from the WearableIntegrationBee
    setWearableDevices([
      {
        id: '1',
        name: 'Apple Watch Series 9',
        type: 'smartwatch',
        manufacturer: 'Apple',
        model: 'Series 9',
        connectionStatus: 'connected',
        batteryLevel: 85,
        lastSync: new Date(Date.now() - 30 * 60 * 1000),
        capabilities: ['heart_rate', 'gps', 'steps', 'calories', 'sleep', 'workout_detection'],
        data: {
          heartRate: 72,
          steps: 8542,
          calories: 2340,
          distance: 6.2,
          activeMinutes: 45
        }
      },
      {
        id: '2',
        name: 'Garmin Forerunner 955',
        type: 'gps_device',
        manufacturer: 'Garmin',
        model: 'Forerunner 955',
        connectionStatus: 'connected',
        batteryLevel: 92,
        lastSync: new Date(Date.now() - 15 * 60 * 1000),
        capabilities: ['gps', 'heart_rate', 'power', 'recovery', 'training_status'],
        data: {
          heartRate: 68,
          steps: 0,
          calories: 1890,
          distance: 0,
          activeMinutes: 0
        }
      }
    ]);

    setRecoveryDevices([
      {
        id: '1',
        name: 'Normatec 3.0',
        type: 'normatec',
        manufacturer: 'Hyperice',
        model: 'Normatec 3.0',
        connectionStatus: 'connected',
        lastSession: new Date(Date.now() - 2 * 60 * 60 * 1000),
        capabilities: ['compression', 'massage', 'recovery_tracking'],
        currentSession: {
          duration: 20,
          intensity: 7,
          mode: 'recovery',
          targetArea: ['legs', 'hips']
        }
      },
      {
        id: '2',
        name: 'Theragun Elite',
        type: 'theragun',
        manufacturer: 'Therabody',
        model: 'Elite',
        connectionStatus: 'disconnected',
        lastSession: new Date(Date.now() - 24 * 60 * 60 * 1000),
        capabilities: ['percussive_therapy', 'massage', 'pain_relief']
      }
    ]);
  };

  const scanForDevices = async () => {
    setIsScanning(true);
    try {
      // Mock device scanning - in production, this would use Bluetooth/WiFi scanning
      setTimeout(() => {
        setIsScanning(false);
        setShowAddDevice(true);
      }, 3000);
    } catch (error) {
      console.error('Error scanning for devices:', error);
      setIsScanning(false);
    }
  };

  const syncDeviceData = async (deviceId: string) => {
    setIsSyncing(true);
    try {
      // Mock data sync - in production, this would call WearableIntegrationBee
      setTimeout(() => {
        setWearableDevices(prev => prev.map(device => 
          device.id === deviceId 
            ? { ...device, lastSync: new Date() }
            : device
        ));
        setIsSyncing(false);
        
        if (onDataSynced) {
          onDataSynced({ deviceId, syncTime: new Date() });
        }
      }, 2000);
    } catch (error) {
      console.error('Error syncing device data:', error);
      setIsSyncing(false);
    }
  };

  const startRecoverySession = async (deviceId: string) => {
    try {
      // Mock recovery session start - in production, this would call RecoveryDeviceBee
      const device = recoveryDevices.find(d => d.id === deviceId);
      if (device) {
        const session = {
          deviceId,
          deviceType: device.type,
          startTime: new Date(),
          settings: {
            intensity: 7,
            duration: 20,
            mode: 'recovery'
          }
        };
        
        if (onRecoverySessionStarted) {
          onRecoverySessionStarted(session);
        }
      }
    } catch (error) {
      console.error('Error starting recovery session:', error);
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'smartwatch': return <Watch className="w-6 h-6" />;
      case 'fitness_tracker': return <Activity className="w-6 h-6" />;
      case 'heart_rate_monitor': return <Heart className="w-6 h-6" />;
      case 'power_meter': return <Zap className="w-6 h-6" />;
      case 'gps_device': return <Target className="w-6 h-6" />;
      case 'massage_chair': return <Moon className="w-6 h-6" />;
      case 'normatec': return <Droplets className="w-6 h-6" />;
      case 'compression_boots': return <Droplets className="w-6 h-6" />;
      case 'sauna': return <Thermometer className="w-6 h-6" />;
      case 'ice_bath': return <Droplets className="w-6 h-6" />;
      case 'theragun': return <Zap className="w-6 h-6" />;
      default: return <Smartphone className="w-6 h-6" />;
    }
  };

  const getConnectionIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'disconnected': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'error': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default: return <AlertTriangle className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />;
    }
  };

  const getBatteryColor = (level: number) => {
    if (level > 50) return 'text-green-600';
    if (level > 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="flex flex-col h-full sculptd-card-elevated sculptd-sheen" style={{
      boxShadow: 'var(--shadow-card-separation)',
      border: '1px solid rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div className="flex items-center justify-end sculptd-container-padding border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center space-x-3">
          <button
            onClick={scanForDevices}
            disabled={isScanning}
            className="sculptd-cta-button flex items-center space-x-3 px-3 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ 
              background: 'var(--gradient-cta-primary)',
              boxShadow: 'var(--shadow-lg)'
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.background = 'var(--gradient-accent-button)';
              (e.target as HTMLElement).style.boxShadow = 'var(--shadow-xl)';
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = 'var(--gradient-cta-primary)';
              (e.target as HTMLElement).style.boxShadow = 'var(--shadow-lg)';
            }}
          >
            <Plus className="w-4 h-4" />
            <span>{isScanning ? 'Scanning...' : 'Add Device'}</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => setActiveTab('wearables')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'wearables'
              ? 'border-b-2'
              : ''
          }`}
          style={{
            color: activeTab === 'wearables' ? 'var(--color-primary)' : 'var(--color-text-muted)',
            borderColor: activeTab === 'wearables' ? 'var(--color-primary)' : 'transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'wearables') {
              e.target.style.color = 'var(--color-text)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'wearables') {
              e.target.style.color = 'var(--color-text-muted)';
            }
          }}
        >
          Wearables
        </button>
        <button
          onClick={() => setActiveTab('recovery')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'recovery'
              ? 'border-b-2'
              : ''
          }`}
          style={{
            color: activeTab === 'recovery' ? 'var(--color-primary)' : 'var(--color-text-muted)',
            borderColor: activeTab === 'recovery' ? 'var(--color-primary)' : 'transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'recovery') {
              e.target.style.color = 'var(--color-text)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'recovery') {
              e.target.style.color = 'var(--color-text-muted)';
            }
          }}
        >
          Recovery
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'analytics'
              ? 'border-b-2'
              : ''
          }`}
          style={{
            color: activeTab === 'analytics' ? 'var(--color-primary)' : 'var(--color-text-muted)',
            borderColor: activeTab === 'analytics' ? 'var(--color-primary)' : 'transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'analytics') {
              e.target.style.color = 'var(--color-text)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'analytics') {
              e.target.style.color = 'var(--color-text-muted)';
            }
          }}
        >
          Analytics
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${
            activeTab === 'settings'
              ? 'border-b-2'
              : ''
          }`}
          style={{
            color: activeTab === 'settings' ? 'var(--color-primary)' : 'var(--color-text-muted)',
            borderColor: activeTab === 'settings' ? 'var(--color-primary)' : 'transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'settings') {
              e.target.style.color = 'var(--color-text)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'settings') {
              e.target.style.color = 'var(--color-text-muted)';
            }
          }}
        >
          Settings
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto sculptd-container-padding">
        {activeTab === 'wearables' && (
          <div className="space-y-6">
            {/* Connected Devices */}
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Connected Wearables</h3>
              <div className="space-y-4">
                {wearableDevices.map((device) => (
                  <div key={device.id} className="sculptd-card-elevated sculptd-sheen rounded-lg sculptd-card-padding">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg" style={{ background: 'var(--color-primary-light)' }}>
                          {getDeviceIcon(device.type)}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{device.name}</h4>
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{device.manufacturer} {device.model}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getConnectionIcon(device.connectionStatus)}
                        <span className="text-sm capitalize" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{device.connectionStatus}</span>
                      </div>
                    </div>

                    {/* Device Data */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Heart className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="text-lg font-semibold text-gray-900">{device.data.heartRate}</div>
                        <div className="text-xs text-gray-500">BPM</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Activity className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="text-lg font-semibold text-gray-900">{device.data.steps.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">Steps</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Zap className="w-4 h-4 text-yellow-600" />
                        </div>
                        <div className="text-lg font-semibold text-gray-900">{device.data.calories}</div>
                        <div className="text-xs text-gray-500">Calories</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Target className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="text-lg font-semibold text-gray-900">{device.data.distance}</div>
                        <div className="text-xs text-gray-500">Miles</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1">
                          <Clock className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="text-lg font-semibold text-gray-900">{device.data.activeMinutes}</div>
                        <div className="text-xs text-gray-500">Minutes</div>
                      </div>
                    </div>

                    {/* Device Status */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-3">
                          <Battery className={`w-4 h-4 ${getBatteryColor(device.batteryLevel)}`} />
                          <span className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{device.batteryLevel}%</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                            Last sync: {device.lastSync.toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => syncDeviceData(device.id)}
                          disabled={isSyncing}
                          className="sculptd-cta-button flex items-center space-x-3 px-3 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                          style={{ 
                            background: 'var(--gradient-cta-primary)',
                            boxShadow: 'var(--shadow-lg)'
                          }}
                          onMouseEnter={(e) => {
                            (e.target as HTMLElement).style.background = 'var(--gradient-accent-button)';
                            (e.target as HTMLElement).style.boxShadow = 'var(--shadow-xl)';
                          }}
                          onMouseLeave={(e) => {
                            (e.target as HTMLElement).style.background = 'var(--gradient-cta-primary)';
                            (e.target as HTMLElement).style.boxShadow = 'var(--shadow-lg)';
                          }}
                        >
                          <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                          <span>Sync</span>
                        </button>
                        <button className="p-2 text-gray-500 hover:text-gray-700 transition-colors">
                          <Settings className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Available Devices */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Devices</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { name: 'Apple Watch', type: 'smartwatch', manufacturer: 'Apple' },
                  { name: 'Garmin Forerunner', type: 'gps_device', manufacturer: 'Garmin' },
                  { name: 'Fitbit Charge', type: 'fitness_tracker', manufacturer: 'Fitbit' },
                  { name: 'Polar H10', type: 'heart_rate_monitor', manufacturer: 'Polar' }
                ].map((device, index) => (
                  <div key={index} className="border rounded-lg sculptd-card-padding transition-colors cursor-pointer" style={{ borderColor: 'var(--color-border)' }} onMouseEnter={(e) => e.target.style.borderColor = 'var(--color-primary)'} onMouseLeave={(e) => e.target.style.borderColor = 'var(--color-border)'}>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg" style={{ background: 'var(--color-background-secondary)' }}>
                        {getDeviceIcon(device.type)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{device.name}</h4>
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{device.manufacturer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'recovery' && (
          <div className="space-y-6">
            {/* Connected Recovery Devices */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recovery Devices</h3>
              <div className="space-y-4">
                {recoveryDevices.map((device) => (
                  <div key={device.id} className="sculptd-card-elevated sculptd-sheen rounded-lg sculptd-card-padding">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          {getDeviceIcon(device.type)}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{device.name}</h4>
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{device.manufacturer} {device.model}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getConnectionIcon(device.connectionStatus)}
                        <span className="text-sm capitalize" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{device.connectionStatus}</span>
                      </div>
                    </div>

                    {/* Current Session */}
                    {device.currentSession && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-green-900">Active Session</h5>
                          <div className="flex items-center space-x-2">
                            <Play className="w-4 h-4 text-green-600" />
                            <span className="text-sm" style={{ color: 'var(--color-success)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Running</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-6 text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          <div>
                            <div className="text-green-800 font-medium">{device.currentSession.duration} min</div>
                            <div className="text-green-600">Duration</div>
                          </div>
                          <div>
                            <div className="text-green-800 font-medium">{device.currentSession.intensity}/10</div>
                            <div className="text-green-600">Intensity</div>
                          </div>
                          <div>
                            <div className="text-green-800 font-medium">{device.currentSession.mode}</div>
                            <div className="text-green-600">Mode</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Device Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                          Last session: {device.lastSession.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {device.connectionStatus === 'connected' ? (
                          <button
                            onClick={() => startRecoverySession(device.id)}
                            className="sculptd-cta-button flex items-center space-x-2"
                          >
                            <Play className="w-4 h-4" />
                            <span>Start Session</span>
                          </button>
                        ) : (
                          <button className="flex items-center space-x-2 px-3 py-2 text-white rounded-lg transition-colors" style={{ background: 'var(--color-text-muted)' }} onMouseEnter={(e) => e.target.style.background = 'var(--color-text)'} onMouseLeave={(e) => e.target.style.background = 'var(--color-text-muted)'}>
                            <Wifi className="w-4 h-4" />
                            <span>Connect</span>
                          </button>
                        )}
                        <button className="p-2 text-gray-500 hover:text-gray-700 transition-colors">
                          <Settings className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recovery Recommendations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recovery Recommendations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="sculptd-card-elevated sculptd-sheen rounded-lg sculptd-card-padding">
                  <div className="flex items-center space-x-2 mb-2">
                    <Moon className="w-5 h-5 text-blue-600" />
                    <h4 className="font-medium text-gray-900">Post-Workout Recovery</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Use Normatec compression therapy for 20 minutes after intense workouts to reduce muscle soreness and improve recovery.
                  </p>
                  <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Start Recovery Session →
                  </button>
                </div>
                <div className="sculptd-card-elevated sculptd-sheen rounded-lg sculptd-card-padding">
                  <div className="flex items-center space-x-2 mb-2">
                    <Sun className="w-5 h-5 text-yellow-600" />
                    <h4 className="font-medium text-gray-900">Morning Recovery</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Use Theragun percussive therapy for 10 minutes in the morning to reduce muscle tension and improve mobility.
                  </p>
                  <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Start Recovery Session →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Device Analytics</h3>
              <p className="text-gray-600 mb-6">
                View detailed analytics and insights from your connected devices
              </p>
              <button className="sculptd-cta-button px-6 py-3">
                View Analytics
              </button>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="text-center py-12">
              <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Device Settings</h3>
              <p className="text-gray-600 mb-6">
                Configure device preferences and connection settings
              </p>
              <button className="sculptd-cta-button px-6 py-3">
                Open Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceIntegrationPanel;
